import cn from 'classnames';
import { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { useVoiceSearch } from 'src/hooks/use-voice-search';

// Game names in the catalog are always English, so recognize speech as English
// regardless of the UI language (otherwise "taboo" gets transcribed as "tabu", etc.).
const RECOGNITION_LANG = 'en-US';

type VoiceSearchButtonProps = {
  /** Receives the recognized text; callers typically push it into the search input. */
  onResult: (transcript: string) => void;
  className?: string;
};

const VoiceSearchButton: FC<VoiceSearchButtonProps> = ({ onResult, className }) => {
  const { t } = useTranslation();
  const { isListening, isSupported, startListening, stopListening } = useVoiceSearch({
    onResult,
    lang: RECOGNITION_LANG,
  });

  // Hide entirely on browsers without the Web Speech API (e.g. Firefox).
  if (!isSupported) return null;

  const toggle = (ev: MouseEvent): void => {
    // Avoid bubbling to parent search containers (which open/focus on click).
    ev.stopPropagation();
    isListening ? stopListening() : startListening();
  };

  return (
    <div
      className={cn('voice_search_btn', { ['listening']: isListening }, className)}
      onClick={toggle}
      role="button"
      aria-pressed={isListening}
      title={isListening ? t('listening') : t('voiceSearch')}
    >
      <SvgIcon src={icons.mic} className="icon" />
    </div>
  );
};

export default VoiceSearchButton;
